import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ORDER_MAX_INDENT,
  checkAnswerAgainstExpected,
  formatOrderAnswer,
  formatOrderRevealProgram,
  getOrderItems,
  indentFromHorizontalDelta,
  parseOrderAnswer,
  resolveShuffledOrderItems,
} from '../utils/workAttemptHelpers';

const PROGRAM_ID = 'assemble-program';
const BANK_ID = 'assemble-bank';

function restrictToVerticalAxis({ transform }) {
  return { ...transform, x: 0 };
}

function clientXFromEvent(event) {
  if (!event) return null;
  if (typeof event.clientX === 'number') return event.clientX;
  if (event.touches?.[0]) return event.touches[0].clientX;
  if (event.changedTouches?.[0]) return event.changedTouches[0].clientX;
  return null;
}

function IndentSlots({ level }) {
  const n = Math.max(0, level);
  if (!n) return null;
  return (
    <span className="assemble-indent-slots" aria-hidden>
      {Array.from({ length: n }, (_, i) => (
        <span key={i} className="assemble-indent-slot" />
      ))}
    </span>
  );
}

function LineCode({ indent, text }) {
  return (
    <div className="assemble-line-code">
      <IndentSlots level={indent} />
      <span className="assemble-line-text">{text}</span>
    </div>
  );
}

function SortableProgramLine({
  row,
  saving,
  onIndent,
  onRemove,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: row.id,
    disabled: saving,
    data: { container: 'program', indent: row.indent, text: row.text },
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`assemble-line${isDragging ? ' is-dragging' : ''}`}
      role="listitem"
    >
      <div className="assemble-line-controls">
        <button
          type="button"
          className="btn btn-outline btn-xs btn-icon-only"
          disabled={saving || row.indent <= 0}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => onIndent(row.id, row.indent - 1)}
          title="Уменьшить отступ"
          aria-label="Уменьшить отступ"
        >
          ◀
        </button>
        <button
          type="button"
          className="btn btn-outline btn-xs btn-icon-only"
          disabled={saving || row.indent >= ORDER_MAX_INDENT}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => onIndent(row.id, row.indent + 1)}
          title="Увеличить отступ"
          aria-label="Увеличить отступ"
        >
          ▶
        </button>
        <button
          type="button"
          className="btn btn-outline btn-xs btn-icon-only"
          disabled={saving}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => onRemove(row.id)}
          title="Убрать"
          aria-label="Убрать"
        >
          ×
        </button>
      </div>
      <div className="assemble-line-drag" {...attributes} {...listeners}>
        <LineCode indent={row.indent} text={row.text} />
      </div>
    </div>
  );
}

function SortableBankItem({ item, saving, onClick }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    disabled: saving,
    data: { container: 'bank', indent: 0, text: item.text },
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li ref={setNodeRef} style={style} className={isDragging ? 'is-dragging' : undefined}>
      <button
        type="button"
        className="assemble-bank-item"
        disabled={saving}
        onClick={onClick}
        {...attributes}
        {...listeners}
      >
        {item.text}
      </button>
    </li>
  );
}

function DroppableBox({ id, className, children, ...rest }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={`${className}${isOver ? ' is-over' : ''}`}
      {...rest}
    >
      {children}
    </div>
  );
}

function snapshotFromLists(assembled, bank) {
  const indent = {};
  assembled.forEach((row) => {
    indent[row.id] = row.indent;
  });
  bank.forEach((item) => {
    indent[item.id] = 0;
  });
  return {
    program: assembled.map((row) => row.id),
    bank: bank.map((item) => item.id),
    indent,
  };
}

function findContainer(id, items) {
  if (id === PROGRAM_ID || id === BANK_ID) {
    return id === PROGRAM_ID ? 'program' : 'bank';
  }
  if (items.program.includes(id)) return 'program';
  if (items.bank.includes(id)) return 'bank';
  return null;
}

function moveToContainer(items, activeId, overId, overContainer) {
  const activeContainer = findContainer(activeId, items);
  if (!activeContainer || !overContainer || activeContainer === overContainer) {
    return items;
  }
  const activeIds = items[activeContainer];
  const overIds = items[overContainer];
  const activeIndex = activeIds.indexOf(activeId);
  if (activeIndex < 0) return items;

  let newIndex;
  if (overId === PROGRAM_ID || overId === BANK_ID) {
    newIndex = overIds.length;
  } else {
    const overIndex = overIds.indexOf(overId);
    newIndex = overIndex >= 0 ? overIndex : overIds.length;
  }

  return {
    ...items,
    [activeContainer]: activeIds.filter((id) => id !== activeId),
    [overContainer]: [
      ...overIds.slice(0, newIndex),
      activeIds[activeIndex],
      ...overIds.slice(newIndex),
    ],
  };
}

/**
 * Сборка программы из строк (тип order): банк + собранный код, отступ по 4 пробела.
 */
export default function AssembleAnswerPanel(props) {
  return (
    <AssembleAnswerPanelInner
      key={`${props.taskKey ?? ''}:${props.shuffleSeed ?? ''}:${props.expectedAnswer ?? ''}`}
      {...props}
    />
  );
}

function AssembleAnswerPanelInner({
  answerSpec,
  expectedAnswer,
  taskKey = '',
  shuffleSeed = '',
  shuffle = true,
  value,
  onChange,
  onCheck,
  onReveal,
  saving = false,
  className = '',
}) {
  const controlled = value !== undefined;
  const [internalAnswer, setInternalAnswer] = useState('');
  const [hint, setHint] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [dragItems, setDragItems] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [liveIndent, setLiveIndent] = useState(0);

  const dragItemsRef = useRef(null);
  const originContainerRef = useRef(null);
  const movedBetweenRef = useRef(false);
  const skipBankClickRef = useRef(false);
  const skipClickTimerRef = useRef(null);
  const startIndentRef = useRef(0);
  const startPointerXRef = useRef(0);
  const liveIndentRef = useRef(0);
  const slotWidthRef = useRef(32);
  const slotProbeRef = useRef(null);

  const items = useMemo(() => getOrderItems(answerSpec), [answerSpec]);
  const allowShuffle = shuffle && answerSpec?.shuffle !== false;
  const seed = shuffleSeed || taskKey;
  const shuffled = useMemo(
    () => resolveShuffledOrderItems(items, { seed, shuffle: allowShuffle }),
    [items, seed, allowShuffle],
  );

  const studentAnswer = controlled ? String(value ?? '') : internalAnswer;
  const assembled = useMemo(() => {
    const byId = new Map(items.map((o) => [o.id, o]));
    return parseOrderAnswer(studentAnswer)
      .map((row) => {
        const item = byId.get(row.id);
        if (!item) return null;
        return { id: item.id, text: item.text, indent: row.indent };
      })
      .filter(Boolean);
  }, [studentAnswer, items]);
  const assembledIds = useMemo(() => new Set(assembled.map((row) => row.id)), [assembled]);
  const bank = useMemo(
    () => shuffled.filter((item) => !assembledIds.has(item.id)),
    [shuffled, assembledIds],
  );
  const itemById = useMemo(() => new Map(items.map((o) => [o.id, o])), [items]);

  const view = dragItems ?? snapshotFromLists(assembled, bank);
  const programRows = view.program
    .map((id) => {
      const item = itemById.get(id);
      if (!item) return null;
      const indent = id === activeId ? liveIndent : (view.indent[id] ?? 0);
      return { id, text: item.text, indent };
    })
    .filter(Boolean);
  const bankRows = view.bank
    .map((id) => itemById.get(id))
    .filter(Boolean);
  const activeItem = activeId ? itemById.get(activeId) : null;

  const hasExpected = expectedAnswer != null && String(expectedAnswer).trim() !== '';
  const hasItems = items.length > 0;

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useLayoutEffect(() => {
    const el = slotProbeRef.current;
    if (!el) return undefined;
    const measure = () => {
      const width = el.getBoundingClientRect().width;
      if (width > 0) slotWidthRef.current = width;
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [hasItems]);

  useEffect(() => () => {
    if (skipClickTimerRef.current) window.clearTimeout(skipClickTimerRef.current);
  }, []);

  useEffect(() => {
    if (!activeId) return undefined;
    function onPointerMove(event) {
      const x = clientXFromEvent(event);
      if (x == null) return;
      const next = indentFromHorizontalDelta(
        startIndentRef.current,
        x - startPointerXRef.current,
        slotWidthRef.current,
      );
      liveIndentRef.current = next;
      setLiveIndent(next);
    }
    window.addEventListener('pointermove', onPointerMove);
    return () => window.removeEventListener('pointermove', onPointerMove);
  }, [activeId]);

  if (!hasItems) return null;

  function setRows(nextRows) {
    const next = formatOrderAnswer(nextRows);
    if (controlled) {
      if (typeof onChange === 'function') onChange(next);
    } else {
      setInternalAnswer(next);
    }
    setHint(null);
  }

  function commitView(next, indentOverrideId, indentOverride) {
    const rows = next.program.map((id) => ({
      id,
      indent: id === indentOverrideId
        ? indentOverride
        : Math.max(0, Math.min(ORDER_MAX_INDENT, next.indent[id] ?? 0)),
    }));
    setRows(rows);
  }

  function addFromBank(id) {
    const item = items.find((o) => o.id === id);
    if (!item || assembledIds.has(id)) return;
    setRows([...assembled, { id: item.id, indent: 0 }]);
  }

  function removeId(id) {
    setRows(assembled.filter((row) => row.id !== id).map((row) => ({ id: row.id, indent: row.indent })));
  }

  function setIndentForId(id, indent) {
    const next = assembled.map((row) => (
      row.id === id
        ? { id: row.id, indent: Math.max(0, Math.min(ORDER_MAX_INDENT, indent)) }
        : { id: row.id, indent: row.indent }
    ));
    setRows(next);
  }

  function handleCheck() {
    if (!hasExpected) {
      setHint('wrong');
      return;
    }
    const ok = checkAnswerAgainstExpected(studentAnswer, expectedAnswer, 'order', answerSpec);
    setHint(ok ? 'ok' : 'wrong');
    if (typeof onCheck === 'function') {
      void onCheck({ ok, studentAnswer });
    }
  }

  function handleRevealToggle() {
    if (showAnswer) {
      setShowAnswer(false);
      return;
    }
    setShowAnswer(true);
    if (typeof onReveal === 'function') {
      void onReveal({ studentAnswer });
    }
  }

  function markSkipBankClick() {
    skipBankClickRef.current = true;
    if (skipClickTimerRef.current) window.clearTimeout(skipClickTimerRef.current);
    skipClickTimerRef.current = window.setTimeout(() => {
      skipBankClickRef.current = false;
      skipClickTimerRef.current = null;
    }, 0);
  }

  function clearDragState() {
    setActiveId(null);
    setDragItems(null);
    dragItemsRef.current = null;
    originContainerRef.current = null;
    movedBetweenRef.current = false;
  }

  function handleDragStart(event) {
    const snap = snapshotFromLists(assembled, bank);
    dragItemsRef.current = snap;
    originContainerRef.current = findContainer(event.active.id, snap);
    movedBetweenRef.current = false;
    setDragItems(snap);
    setActiveId(event.active.id);
    const startIndent = originContainerRef.current === 'program'
      ? (snap.indent[event.active.id] ?? 0)
      : 0;
    startIndentRef.current = startIndent;
    liveIndentRef.current = startIndent;
    setLiveIndent(startIndent);
    startPointerXRef.current = clientXFromEvent(event.activatorEvent) ?? 0;
  }

  function handleDragOver(event) {
    const { active, over } = event;
    if (!over) return;
    setDragItems((prev) => {
      const base = prev ?? dragItemsRef.current;
      if (!base) return prev;
      const overContainer = findContainer(over.id, base);
      if (!overContainer) return prev;
      const activeContainer = findContainer(active.id, base);
      if (activeContainer && activeContainer !== overContainer) {
        movedBetweenRef.current = true;
      }
      const next = moveToContainer(base, active.id, over.id, overContainer);
      dragItemsRef.current = next;
      return next;
    });
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    const current = dragItemsRef.current;
    markSkipBankClick();
    if (!current || !over) {
      clearDragState();
      return;
    }

    let next = current;
    const overContainer = findContainer(over.id, next);
    if (overContainer) {
      next = moveToContainer(next, active.id, over.id, overContainer);
    }
    const container = findContainer(active.id, next);
    if (
      !movedBetweenRef.current
      && container
      && overContainer
      && container === overContainer
    ) {
      const ids = next[container];
      const oldIndex = ids.indexOf(active.id);
      const newIndex = over.id === PROGRAM_ID || over.id === BANK_ID
        ? ids.length - 1
        : ids.indexOf(over.id);
      if (oldIndex >= 0 && newIndex >= 0 && oldIndex !== newIndex) {
        next = { ...next, [container]: arrayMove(ids, oldIndex, newIndex) };
      }
    }

    commitView(next, active.id, container === 'program' ? liveIndentRef.current : 0);
    clearDragState();
  }

  function handleDragCancel() {
    markSkipBankClick();
    clearDragState();
  }

  function handleBankClick(id) {
    if (skipBankClickRef.current) {
      skipBankClickRef.current = false;
      return;
    }
    addFromBank(id);
  }

  const revealText = formatOrderRevealProgram(expectedAnswer, items);
  const showBank = bankRows.length > 0 || Boolean(activeId);

  const hintNode =
    hint === 'ok' ? (
      <span className="work-answer-hint work-answer-hint-ok">Верно</span>
    ) : hint === 'wrong' ? (
      <span className="work-answer-hint work-answer-hint-wrong">Неверно</span>
    ) : null;

  return (
    <div className={`assemble-answer-panel ${className}`.trim()}>
      <div className="assemble-answer-panel-label">
        Соберите программу: перетащите строки, вправо и влево — отступ.
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        modifiers={[restrictToVerticalAxis]}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <DroppableBox id={PROGRAM_ID} className="assemble-program" role="list" aria-label="Собранная программа">
          <span ref={slotProbeRef} className="assemble-indent-slot assemble-indent-slot-probe" aria-hidden />
          <SortableContext items={view.program} strategy={verticalListSortingStrategy}>
            {programRows.length === 0 ? (
              <div className="assemble-program-empty">
                Перетащите строку сюда или нажмите на неё внизу
              </div>
            ) : (
              programRows.map((row) => (
                <SortableProgramLine
                  key={row.id}
                  row={row}
                  saving={saving}
                  onIndent={setIndentForId}
                  onRemove={removeId}
                />
              ))
            )}
          </SortableContext>
        </DroppableBox>
        {showBank ? (
          <>
            <div className="assemble-answer-panel-label assemble-bank-label">
              {bankRows.length > 0 ? 'Неиспользованные строки' : 'Отпустите здесь, чтобы убрать строку'}
            </div>
            <DroppableBox id={BANK_ID} className="assemble-bank-wrap">
              <SortableContext items={view.bank} strategy={verticalListSortingStrategy}>
                <ul className="assemble-bank">
                  {bankRows.length === 0 ? (
                    <li className="assemble-bank-empty">Перетащите строку обратно</li>
                  ) : (
                    bankRows.map((item) => (
                      <SortableBankItem
                        key={item.id}
                        item={item}
                        saving={saving}
                        onClick={() => handleBankClick(item.id)}
                      />
                    ))
                  )}
                </ul>
              </SortableContext>
            </DroppableBox>
          </>
        ) : null}
        {createPortal(
          <DragOverlay dropAnimation={null}>
            {activeItem ? (
              <div className="assemble-line assemble-line-overlay">
                <LineCode indent={liveIndent} text={activeItem.text} />
              </div>
            ) : null}
          </DragOverlay>,
          document.body,
        )}
      </DndContext>
      <div className="work-student-answer-actions">
        <button
          type="button"
          className="btn btn-outline btn-sm"
          disabled={saving || !hasExpected}
          onClick={handleCheck}
        >
          {saving ? '…' : 'Проверить'}
        </button>
        {hasExpected ? (
          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={saving && !showAnswer}
            onClick={handleRevealToggle}
          >
            {showAnswer ? 'Скрыть ответ' : saving ? '…' : 'Показать ответ'}
          </button>
        ) : null}
        {hintNode}
      </div>
      {showAnswer && revealText ? (
        <div className="answer-block task-answer-check-reveal">
          <pre className="assemble-reveal-text">{revealText}</pre>
        </div>
      ) : null}
    </div>
  );
}
